/**
 * SearchableSelect.tsx — Spec 206. A lightweight token-styled searchable combobox
 * (kit Input + a filtered dropdown). Used for the Job Activities "App / source"
 * filter so you can search the registered apps instead of typing free text.
 * Constitution XLV (tokens + kit Input + lucide). Author: Peter Dobler. 2026-06-30.
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { Input } from '@/components/ui';

export interface SearchableOption {
  value: string;
  label: string;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder,
  allLabel = 'All',
}: {
  options: SearchableOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  allLabel?: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selectedLabel = options.find((o) => o.value === value)?.label ?? '';
  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const pick = (v: string): void => { onChange(v); setQuery(''); setOpen(false); };

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <Input
          placeholder={placeholder}
          value={open ? query : selectedLabel}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { setOpen(true); setQuery(''); }}
        />
        {value ? (
          <button
            type="button"
            aria-label="Clear"
            onMouseDown={(e) => { e.preventDefault(); pick(''); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--color-text-secondary)] hover:text-[var(--color-text-secondary)]"
          >
            <X size={14} />
          </button>
        ) : (
          <button
            type="button"
            aria-label="Toggle options"
            onMouseDown={(e) => { e.preventDefault(); setQuery(''); setOpen((o) => !o); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
          >
            <ChevronDown size={14} />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] py-1 shadow-lg">
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); pick(''); }}
            className="block w-full px-3 py-1.5 text-left text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)]"
          >
            {allLabel}
          </button>
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-[var(--color-text-secondary)]">No matches</div>
          ) : (
            filtered.map((o) => (
              <button
                key={o.value}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(o.value); }}
                className={`block w-full truncate px-3 py-1.5 text-left text-sm hover:bg-[var(--color-bg-subtle)] ${
                  o.value === value ? 'font-medium text-[var(--color-accent)]' : 'text-[var(--color-text-primary)]'
                }`}
                title={o.label}
              >
                {o.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
